import { isEnglishTestRoute, t, useLanguage } from '@/lib/i18n'
import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { LucentShell } from '@/components/shell/LucentShell'
import { RequireAuth } from '@/features/auth/RequireAuth'
import { RequireRole } from '@/features/auth/RequireRole'
import { LanguageToggle } from '@/components/shell/LanguageToggle'
import { ReleaseBanner } from '@/components/shell/ReleaseBanner'
import { StaffTicketBoundary } from '@/features/staff-tickets/StaffTicketBoundary'

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/features/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'))
const ActivateAccountPage = lazy(() => import('@/features/auth/ActivateAccountPage'))
const ActivateStaffPage = lazy(() => import('@/features/auth/ActivateStaffPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const ExamsPage = lazy(() => import('@/features/exams/ExamsPage'))
const RunnerPage = lazy(() => import('@/features/runner/RunnerPage'))
const ResultsPage = lazy(() => import('@/features/results/ResultsPage'))
const ResultDetailPage = lazy(() => import('@/features/results/ResultDetailPage'))
const HistoryPage = lazy(() => import('@/features/history/HistoryPage'))
const LeaderboardPage = lazy(() => import('@/features/leaderboard/LeaderboardPage'))
const AchievementsPage = lazy(() => import('@/features/achievements/AchievementsPage'))
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))
const PlanPage = lazy(() => import('@/features/plan/PlanPage'))
const AdminShell = lazy(() => import('@/features/admin/AdminShell'))
const AdminExamsListPage = lazy(() => import('@/features/admin/AdminExamsListPage'))
const AdminExamsArchivedPage = lazy(() => import('@/features/admin/AdminExamsArchivedPage'))
const AdminQuestionBankPage = lazy(() => import('@/features/admin/AdminQuestionBankPage'))
const AdminExamWizardPage = lazy(() => import('@/features/admin/AdminExamWizardPage'))
const AdminSubmissionsPage = lazy(() => import('@/features/admin/review/AdminSubmissionsPage'))
const AdminResultReviewPage = lazy(() => import('@/features/admin/review/AdminResultReviewPage'))
const ReviewQueuePage = lazy(() => import('@/features/admin/review-queue/ReviewQueuePage'))
const ReviewItemPage = lazy(() => import('@/features/admin/review-queue/ReviewItemPage'))
const AdminDashboardPage = lazy(() => import('@/features/admin/overview/AdminDashboardPage'))
const AdminDraftsRedirect = lazy(() => import('@/features/admin/AdminDraftsRedirect'))
const AdminStudentsListPage = lazy(() => import('@/features/admin/people/StudentsListPage'))
const AdminStudentDetailPage = lazy(() => import('@/features/admin/people/StudentDetailPage'))
const AdminAddStudentPage = lazy(() => import('@/features/admin/people/AddStudentPage'))
const AdminsListPage = lazy(() => import('@/features/admin/people/AdminsListPage'))
const AdminAddAdminPage = lazy(() => import('@/features/admin/people/AddAdminPage'))
const AdminEmployeeDetailPage = lazy(() => import('@/features/admin/people/EmployeeDetailPage'))
const AdminBulkUploadRetiredPage = lazy(() => import('@/features/admin/AdminBulkUploadRetiredPage'))
const AdminAnnouncementsPage = lazy(() => import('@/features/admin/AdminAnnouncementsPage'))
const NotFoundPage = lazy(() => import('@/features/system/NotFoundPage'))
const LandingPage = lazy(() => import('@/features/landing/LandingPage'))
const ScoreCalculatorsPage = lazy(() => import('@/features/calculators/ScoreCalculatorsPage'))
const StudyDestinationsPage = lazy(() => import('@/features/destinations/StudyDestinationsPage'))
const KeepTalkingPage = lazy(() => import('@/features/keep-talking/KeepTalkingPage'))
const TicketInboxPage = lazy(() => import('@/features/staff-tickets/TicketPages').then((module) => ({ default: module.TicketInboxPage })))
const TicketThreadPage = lazy(() => import('@/features/staff-tickets/TicketPages').then((module) => ({ default: module.TicketThreadPage })))

/**
 * Keep Talking is a first-class student surface when the build-time flag is
 * exactly 'true'. An absent, empty or misspelled value leaves the route
 * unregistered, so `/keep-talking` is an honest 404 rather than a half-wired
 * page. Production ships the flag on; do not flip it off to "be safe".
 */
const keepTalkingEnabled = import.meta.env.VITE_ENABLE_KEEP_TALKING === 'true'

function PageFallback() {
  useLanguage()
  return <div className="app-loading" role="status">{t("Loading your journal…")}</div>
}

function InterfaceControls() {
  const { pathname } = useLocation()
  const exam = isEnglishTestRoute(pathname)
  return <div className={exam ? 'interface-controls interface-controls--exam' : 'interface-controls'}>
    <LanguageToggle />
    <ReleaseBanner />
  </div>
}

export function AppRouter() {
  useLanguage()
  return (
    <BrowserRouter>
      <div className="interface-frame">
      <InterfaceControls />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/activate" element={<ActivateAccountPage />} />
          <Route path="/admin/activate" element={<ActivateStaffPage />} />
          <Route path="/ielts-score-calculator" element={<ScoreCalculatorsPage />} />
          <Route path="/where-can-my-score-take-me" element={<StudyDestinationsPage />} />
          <Route element={<RequireAuth />}>
            <Route element={<RequireRole role="student" />}>
              <Route element={<LucentShell />}>
                <Route path="/home" element={<DashboardPage />} />
                <Route path="/dashboard" element={<Navigate to="/home" replace />} />
                <Route path="/exams-library" element={<ExamsPage />} />
                <Route path="/my-exams" element={<HistoryPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/results" element={<ResultsPage />} />
                <Route path="/results/:resultId" element={<ResultDetailPage />} />
                <Route path="/achievements" element={<AchievementsPage />} />
                <Route path="/plan" element={<PlanPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                {keepTalkingEnabled && <Route path="/keep-talking" element={<KeepTalkingPage />} />}
              </Route>
              <Route path="/test/:id" element={<RunnerPage />} />
            </Route>
            <Route element={<RequireRole role="admin" />}>
              <Route element={<StaffTicketBoundary />}>
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/exams" element={<Navigate to="/admin/exams/list" replace />} />
              {/* Every built admin surface lives inside the admin shell. Only
                  redirects and the no-write runner preview sit outside it. */}
              <Route element={<AdminShell />}>
                <Route path="/admin/support" element={<TicketInboxPage />} />
                <Route path="/admin/support/:ticketId" element={<TicketThreadPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/exams/list" element={<AdminExamsListPage />} />
                <Route path="/admin/exams/create" element={<AdminExamWizardPage />} />
                <Route path="/admin/exams/edit/:id" element={<AdminExamWizardPage />} />
                <Route path="/admin/exams/archived" element={<AdminExamsArchivedPage />} />
                <Route path="/admin/questions" element={<AdminQuestionBankPage />} />
                <Route path="/admin/exams/bulk-upload" element={<AdminBulkUploadRetiredPage />} />
                <Route path="/admin/submissions" element={<AdminSubmissionsPage />} />
                <Route path="/admin/results/:id" element={<AdminResultReviewPage />} />
                <Route path="/admin/review-queue" element={<ReviewQueuePage />} />
                <Route path="/admin/review-queue/:itemId" element={<ReviewItemPage />} />
                <Route path="/admin/students/list" element={<AdminStudentsListPage />} />
                <Route path="/admin/students/add" element={<AdminAddStudentPage />} />
                {/* After `add`, so `/admin/students/add` is never read as an id. */}
                <Route path="/admin/students/:id" element={<AdminStudentDetailPage />} />
                <Route path="/admin/admins/list" element={<AdminsListPage />} />
                <Route path="/admin/admins/add" element={<AdminAddAdminPage />} />
                {/* After `add` and `list`, so neither is ever read as an id. */}
                <Route path="/admin/admins/:employeeId" element={<AdminEmployeeDetailPage />} />
                <Route path="/admin/announcements" element={<AdminAnnouncementsPage />} />
              </Route>
              {/* Not `<Navigate to="…?status=unpublished">`: a constant target
                  discards the rest of the incoming query. */}
              <Route path="/admin/exams/drafts" element={<AdminDraftsRedirect />} />
              <Route path="/admin/students" element={<Navigate to="/admin/students/list" replace />} />
              <Route path="/admin/admins" element={<Navigate to="/admin/admins/list" replace />} />
              <Route path="/admin/tests/:id/preview" element={<RunnerPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </div>
    </BrowserRouter>
  )
}
